import React from "react";

/**
 * Page-level header used across the signed-in app.
 *
 * Hybrid pastel accent treatment:
 *   - overline tinted with --quill-warm (peach)
 *   - title uses a subtle warm-cream tint via gradient text
 *   - bottom border carries a tiny mint-to-peach seam at the left edge
 *   - optional `ornament` slot for floating 3D brand objects (e.g. DollarBill)
 *
 * Responsive: tighter padding on mobile so content has breathing room.
 */
const PageHeader = ({ overline, title, description, actions, ornament, testId }) => (
  <div
    className="px-4 md:px-8 py-6 md:py-8 flex items-start md:items-end justify-between gap-4 md:gap-6 flex-wrap relative"
    data-testid={testId}
    style={{ borderBottom: "1px solid #35271e" }}
  >
    {/* signature red margin rule — echoes the letter aesthetic from the landing */}
    <div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        left: "20px",
        top: "24px",
        bottom: "24px",
        width: "1px",
        background:
          "linear-gradient(180deg, transparent 0%, hsl(0 65% 55% / 0.55) 20%, hsl(0 65% 55% / 0.7) 50%, hsl(0 65% 55% / 0.55) 80%, transparent 100%)",
      }}
    />
    {/* warm seam — peach to mint to transparent */}
    <div
      aria-hidden
      className="absolute bottom-0 left-0 h-px pointer-events-none"
      style={{
        width: "min(420px, 60%)",
        background:
          "linear-gradient(90deg, hsl(var(--quill-warm) / 0.7) 0%, hsl(var(--quill-accent) / 0.45) 50%, transparent 100%)",
      }}
    />
    <div className="min-w-0 flex-1 pl-6 md:pl-8">
      {overline && (
        <div
          className="text-[10px] uppercase tracking-[0.22em] mb-2"
          style={{ color: "hsl(var(--quill-warm))" }}
        >
          {overline}
        </div>
      )}
      <h1
        className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tighter"
        style={{
          background:
            "linear-gradient(180deg, #FFFFFF 0%, hsl(var(--quill-info)) 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {title}
      </h1>
      {description && (
        <p className="text-zinc-400 text-sm mt-2 max-w-xl">{description}</p>
      )}
    </div>
    {ornament && (
      <div
        className="hidden lg:block absolute pointer-events-none"
        style={{ right: "200px", top: "50%", transform: "translateY(-50%)" }}
      >
        {ornament}
      </div>
    )}
    {actions && <div className="flex items-center gap-2 shrink-0 relative z-10">{actions}</div>}
  </div>
);

export default PageHeader;
